// /src/pages/ToolDetailPage.jsx
import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, ExternalLink, Check } from 'lucide-react';
import { cardCls, headingCls, bodyText, mutedText, ctaPrimary, ctaSecondary, sectionLabel, pageWrap, sectionInner } from '../components/styles';
import { tools } from '../data/tools';

export default function ToolDetailPage() {
  const { slug } = useParams();
  const tool = tools.find((t) => t.slug === slug);

  if (!tool) return <Navigate to="/tools" replace />;

  return (
    <div className={pageWrap}>
      {/* Hero with dot grid */}
      <section className="relative py-12 overflow-hidden" style={{ background: 'var(--bg)' }}>
        <div className="absolute inset-0 bg-dot-grid opacity-20" />
        <div className={`relative z-10 ${sectionInner}`}>
          <div className="max-w-6xl animate-stagger">
            <Link
              to="/tools"
              className="text-[var(--accent)] font-semibold inline-flex items-center gap-1 hover:opacity-80 mb-6 block"
            >
              <ArrowLeft size={16} /> All Platforms
            </Link>

            <h2 className={`${sectionLabel} mb-4`}>{'// platform'}</h2>

            <div className="flex items-center gap-4 flex-wrap mb-4">
              <h1 className={`text-4xl sm:text-5xl ${headingCls}`}>{tool.name}</h1>
              <span
                className="font-mono text-xs font-semibold px-4 py-2 rounded-lg border border-[color:var(--border)] bg-[var(--bg2)]"
                style={{
                  color: tool.status === 'Production' ? '#a7df6a' : '#0abfda',
                }}
              >
                {tool.status.toUpperCase()}
              </span>
            </div>
            <p className={`text-xl ${mutedText}`}>{tool.tagline}</p>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Metrics strip */}
      {tool.metrics && (
        <>
          <section className="py-10" style={{ background: 'var(--bg2)' }}>
            <div className={sectionInner}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl">
                {tool.metrics.map((m, i) => (
                  <div key={i} className={`${cardCls} p-6`}>
                    <div className="metric-value">{m.value}</div>
                    <div className={`text-sm ${mutedText} mt-1`}>{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <hr className="section-rule" />
        </>
      )}

      {/* Why it matters */}
      {tool.whyItMatters && (
        <>
          <section className="py-10" style={{ background: 'var(--bg)' }}>
            <div className={sectionInner}>
              <div className="max-w-6xl">
                <h2 className={`${sectionLabel} mb-6`}>{'// why this exists'}</h2>
                <div
                  className="rounded-lg p-8"
                  style={{
                    background: 'linear-gradient(135deg, rgba(10,191,218,0.1), rgba(193,232,111,0.1))',
                    border: '1px solid var(--border)',
                  }}
                >
                  <p className={`text-lg ${bodyText} leading-relaxed`}>{tool.whyItMatters}</p>
                </div>
              </div>
            </div>
          </section>
          <hr className="section-rule" />
        </>
      )}

      {/* Overview */}
      <section className="py-10" style={{ background: tool.whyItMatters ? 'var(--bg2)' : 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// overview'}</h2>
            <p className={`text-lg ${bodyText} leading-relaxed`}>{tool.description}</p>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Architecture - terminal window */}
      {tool.architecture && (
        <>
          <section className="py-10" style={{ background: tool.whyItMatters ? 'var(--bg)' : 'var(--bg2)' }}>
            <div className={sectionInner}>
              <div className="max-w-6xl">
                <h2 className={`${sectionLabel} mb-6`}>{'// architecture'}</h2>
                <div className="terminal-window">
                  <div className="terminal-titlebar">
                    <div className="terminal-dot" style={{ background: '#f85149' }} />
                    <div className="terminal-dot" style={{ background: '#f0883e' }} />
                    <div className="terminal-dot" style={{ background: '#3fb950' }} />
                    <span className="text-xs text-[#6e7681] ml-2 font-mono">{tool.name.toLowerCase()} --layers</span>
                  </div>
                  <div className="terminal-body">
                    {tool.architecture.map((layer, i) => (
                      <div key={i} className={i > 0 ? 'mt-3' : ''}>
                        <div>
                          <span className="prompt">[{String(i + 1).padStart(2, '0')}]</span>{' '}
                          <span className="flag">{layer.layer}</span>
                        </div>
                        <div className="comment ml-6"># {layer.detail}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
          <hr className="section-rule" />
        </>
      )}

      {/* Key Features */}
      <section className="py-10" style={{ background: 'var(--bg2)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// features'}</h2>
            <div className={`${cardCls} p-8`}>
              <div className="grid md:grid-cols-2 gap-4">
                {tool.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check className="text-[var(--accent-2)] flex-shrink-0" size={20} />
                    <span className={bodyText}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Tech Stack */}
      {tool.techStack && (
        <>
          <section className="py-10" style={{ background: 'var(--bg)' }}>
            <div className={sectionInner}>
              <div className="max-w-6xl">
                <h2 className={`${sectionLabel} mb-6`}>{'// tech stack'}</h2>
                <div className="flex flex-wrap gap-3">
                  {tool.techStack.map((tech) => (
                    <span
                      key={tech}
                      className="px-4 py-2 rounded-lg text-sm font-semibold border border-[color:var(--border)] bg-[var(--bg2)] text-[var(--text)]"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <hr className="section-rule" />
        </>
      )}

      {/* HADES demo embed */}
      {tool.slug === 'hades' && tool.demoUrl && (
        <>
          <section className="py-10" style={{ background: 'var(--bg2)' }}>
            <div className={sectionInner}>
              <div className="max-w-6xl">
                <h2 className={`${sectionLabel} mb-6`}>{'// live demo'}</h2>
                <div className={`${cardCls} p-8`}>
                  <h3 className={`text-2xl ${headingCls} mb-3`}>Run a live scan</h3>
                  <p className={`${bodyText} leading-relaxed mb-6 max-w-2xl`}>
                    The hosted HADES dashboard lets you upload a file, watch the detection pipeline run,
                    and read the full threat report: YARA hits, ML ensemble score, MITRE ATT&amp;CK mappings,
                    and the hash-chained evidence log. It opens in a new tab.
                  </p>
                  <a href={tool.demoUrl} target="_blank" rel="noopener noreferrer" className={ctaPrimary}>
                    <ExternalLink size={20} /> Launch the HADES demo
                  </a>
                </div>
              </div>
            </div>
          </section>
          <hr className="section-rule" />
        </>
      )}

      {/* CTAs */}
      <section className="py-10" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="flex flex-wrap gap-4 max-w-6xl">
            {tool.docs && (
              <a href={tool.docsUrl} target="_blank" rel="noopener noreferrer" className={ctaPrimary}>
                <BookOpen size={20} /> Documentation
              </a>
            )}
            {tool.secondaryDocs && (
              <a
                href={tool.secondaryDocs.url}
                target="_blank"
                rel="noopener noreferrer"
                className={ctaSecondary}
              >
                <BookOpen size={20} /> {tool.secondaryDocs.label}
              </a>
            )}
            {tool.demo && tool.demoUrl && (
              <a href={tool.demoUrl} target="_blank" rel="noopener noreferrer" className={ctaSecondary}>
                <ExternalLink size={20} /> Open Demo
              </a>
            )}
            <Link to="/contact" className={ctaSecondary}>
              Discuss a deployment
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
